import { TReportType } from "../../types/reporting.types";
import { getFindingStatus } from "./findingFilters";
import { getReportAuthorEmail } from "./reportAuthor";

// The items a report type lists on its page and in its drawer, and what a
// report page needs to open one of them in the drawer.
export type ReportItemList = {
	// Form field that holds the items.
	name: "findings" | "follow_up_actions";
	// Field that identifies an item within its report.
	numberField: "finding_number" | "action_number";
	// A field every item of the list shows, to scroll the item into view.
	scrollField: string;
	// Drawer tab that shows the list.
	tabKey: string;
	// Label of the report page's button that opens the list.
	editLabel: string;
};

const FINDINGS: ReportItemList = {
	name: "findings",
	numberField: "finding_number",
	scrollField: "finding_type",
	tabKey: "findings",
	editLabel: "Edit findings",
};

const FOLLOW_UP_ACTIONS: ReportItemList = {
	name: "follow_up_actions",
	numberField: "action_number",
	scrollField: "description",
	tabKey: "follow_up_actions",
	editLabel: "Edit actions",
};

// Lessons learned has no item list.
export const REPORT_ITEM_LISTS: Partial<Record<TReportType, ReportItemList>> = {
	audit: FINDINGS,
	incident_report: FOLLOW_UP_ACTIONS,
	moc: FOLLOW_UP_ACTIONS,
	ofi: FOLLOW_UP_ACTIONS,
	non_conformance: FOLLOW_UP_ACTIONS,
};

export const getReportEditLabel = (reportType: TReportType) =>
	REPORT_ITEM_LISTS[reportType]?.editLabel ?? "Edit";

// An item picked on a report page: its position in the page's copy of the
// list, and its number, which still finds it if the list has changed since.
export type ReportItemTarget = {
	index: number;
	number?: string;
};

// The signed-in user, as far as these rules need to know.
export type ReportViewer = {
	email?: string | null;
	isSuperAdmin: boolean;
	// Admin of the tenant the user is signed in to.
	isTenantAdmin: boolean;
	// Key of the tenant the user is signed in to.
	tenantKey?: string | null;
	// Signed in to the master tenant, whose admins look after every tenant.
	isMasterTenant: boolean;
};

// The fields of a finding or follow-up action that these rules read.
export type ReportItem = {
	responsible?: string;
	status?: string;
	already_completed?: boolean;
	finding_number?: string;
	action_number?: string;
};

// The fields of a report that these rules read.
export type ReportRecord = {
	_id?: string;
	operator?: string | null;
	status?: string | null;
	open_status?: string | null;
	author?: string | null;
	primary_author?: string | null;
	additional_authors?: (string | null)[] | null;
	reviewer?: string | null;
	commenter?: string | null;
	findings?: ReportItem[] | null;
	follow_up_actions?: ReportItem[] | null;
};

// Whose turn it is at each stage of a report. Lessons learned also lets the
// commenter approve during final review.
const AUTHOR_STAGES = new Set(["draft", "changes_required"]);
const REVIEWER_STAGES = new Set([
	"in_review",
	"final_review",
	"ready_to_close",
]);
const RESPONSIBLE_STAGES = new Set(["in_progress", "revision_required"]);
const COMMENTER_STAGES = new Set(["final_review"]);

// "draft", the open report's sub-status, or null once the report is closed.
const getStage = (report: ReportRecord) => {
	if (report.status === "draft") return "draft";
	if (report.status === "open") return report.open_status ?? null;
	return null;
};

const isSameEmail = (value: unknown, email: string) =>
	typeof value === "string" && value.toLowerCase() === email;

const isReportAdmin = (report: ReportRecord, viewer: ReportViewer) => {
	if (viewer.isSuperAdmin) return true;
	if (!viewer.isTenantAdmin) return false;
	return (
		viewer.isMasterTenant ||
		(!!report.operator && report.operator === viewer.tenantKey)
	);
};

const hasReportRole = (
	reportType: TReportType,
	report: ReportRecord,
	email: string,
	stage: string
) => {
	const authors = [
		getReportAuthorEmail(report),
		...(report.additional_authors ?? []),
	];
	if (
		AUTHOR_STAGES.has(stage) &&
		authors.some((author) => isSameEmail(author, email))
	) {
		return true;
	}
	if (REVIEWER_STAGES.has(stage) && isSameEmail(report.reviewer, email)) {
		return true;
	}
	return (
		reportType === "lesson_learned" &&
		COMMENTER_STAGES.has(stage) &&
		isSameEmail(report.commenter, email)
	);
};

const isOwnOpenItem = (item: ReportItem | null | undefined, email: string) =>
	!!item &&
	isSameEmail(item.responsible, email) &&
	getFindingStatus(item) !== "completed";

const getReportItems = (
	reportType: TReportType,
	report: ReportRecord
): ReportItem[] => {
	const list = REPORT_ITEM_LISTS[reportType];
	const items = list ? report[list.name] : undefined;
	return Array.isArray(items) ? items : [];
};

// "all": the viewer can act on the whole report and every item in it.
// "own": only on items they are responsible for. "none": on nothing.
type Access = { scope: "all" | "own" | "none"; email: string };

const getAccess = (
	reportType: TReportType,
	report: ReportRecord | null | undefined,
	viewer: ReportViewer
): Access => {
	const email = viewer.email?.toLowerCase() ?? "";
	if (!report) return { scope: "none", email };
	if (isReportAdmin(report, viewer)) return { scope: "all", email };
	const stage = getStage(report);
	if (!email || !stage) return { scope: "none", email };
	if (hasReportRole(reportType, report, email, stage)) {
		return { scope: "all", email };
	}
	return {
		scope: RESPONSIBLE_STAGES.has(stage) ? "own" : "none",
		email,
	};
};

// Whether the report's page offers to open it in the drawer: the viewer can
// change something in it now.
export function canWorkOnReport(
	reportType: TReportType,
	report: ReportRecord | null | undefined,
	viewer: ReportViewer
): boolean {
	const { scope, email } = getAccess(reportType, report, viewer);
	if (scope === "own" && report) {
		return getReportItems(reportType, report).some((item) =>
			isOwnOpenItem(item, email)
		);
	}
	return scope === "all";
}

// Whether one item's card offers to open that item in the drawer.
export function canWorkOnReportItem(
	reportType: TReportType,
	report: ReportRecord | null | undefined,
	item: ReportItem | null | undefined,
	viewer: ReportViewer
): boolean {
	const { scope, email } = getAccess(reportType, report, viewer);
	if (scope === "own") return isOwnOpenItem(item, email);
	return scope === "all";
}

// Where a picked item is in another copy of the list: at the same index if
// that still holds it, otherwise the first item with its number; -1 if gone.
export function findReportItemIndex(
	items: unknown,
	target: ReportItemTarget,
	numberField: string
): number {
	if (!Array.isArray(items)) return -1;
	const { index, number } = target;
	const isInRange =
		Number.isInteger(index) && index >= 0 && index < items.length;
	if (!number) return isInRange ? index : -1;
	if (isInRange && items[index]?.[numberField] === number) return index;
	return items.findIndex((item) => item?.[numberField] === number);
}
